import React, {useState, useEffect, useMemo} from 'react';

import {observer} from 'mobx-react';
import Dialog from '@mui/material/Dialog';
import CloseIcon from '@mui/icons-material/Close';

import {useStores} from '../../../Hooks';
import {accessItemTypeEnum} from '../../../serverClientShared/sharedEnums';
import SearchBar from '../AccessModal/SearchBar';
import AccessFooter from '../AccessModal/AccessFooter';
import {FolderRow, ItemRow} from './ConnectedFlowsRows';
import {buildChildrenIndex, collectAllProcedureIds, matchesSearch, computeFolderState, getRouteDepth} from './ConnectedFlowsHelpers';

const dialogPaperProps = {className: 'rounded-input', style: {width: 480, height: '70vh', display: 'flex', flexDirection: 'column'}};

const ConnectedFlowsModal = observer(({isOpen, onClose, onSave, initialSelected, procedures, folders, projectName}) => {
    const {generalStore, modalStore} = useStores();
    const [selected, setSelected] = useState(new Set());
    const [expanded, setExpanded] = useState(new Set(['root']));
    const [search, setSearch] = useState('');
    const [isSaving, setIsSaving] = useState(false);

    useEffect(() => {
        if (!isOpen) return;
        setSelected(new Set(initialSelected));
        setExpanded(new Set(['root']));
        setSearch('');
    }, [isOpen, initialSelected]);

    const childrenIndex = useMemo(() => buildChildrenIndex(folders, procedures), [folders, procedures]);

    const toggleItem = (id) => {
        const next = new Set(selected);
        next.has(id) ? next.delete(id) : next.add(id);
        setSelected(next);
    };

    const toggleFolder = (folderId) => {
        const all = collectAllProcedureIds(childrenIndex, folderId);
        const next = new Set(selected);
        const isFull = computeFolderState(childrenIndex, folderId, selected) === 'checked';
        all.forEach((id) => isFull ? next.delete(id) : next.add(id));
        setSelected(next);
    };

    const toggleExpand = (folderId) => {
        const next = new Set(expanded);
        next.has(folderId) ? next.delete(folderId) : next.add(folderId);
        setExpanded(next);
    };

    const renderChildren = (parentId) => {
        const items = childrenIndex.get(parentId) || [];
        const query = search.trim().toLowerCase();
        return items.map((child) => {
            const depth = getRouteDepth(child.raw?.NestedFolderRoute);
            if (child.kind === accessItemTypeEnum.FOLDER) {
                if (query && !matchesSearch(childrenIndex, child.id, query) && !child.name?.toLowerCase().includes(query)) return null;
                const isExpanded = expanded.has(child.id) || !!query;
                return (
                    <React.Fragment key={child.id}>
                        <FolderRow folder={child} depth={depth} isExpanded={isExpanded} checkboxState={computeFolderState(childrenIndex, child.id, selected)} onToggleExpand={() => toggleExpand(child.id)} onToggleCheck={() => toggleFolder(child.id)} />
                        {isExpanded && renderChildren(child.id)}
                    </React.Fragment>
                );
            }
            if (query && !child.name?.toLowerCase().includes(query)) return null;
            return <ItemRow key={child.id} item={child} depth={depth} isChecked={selected.has(child.id)} onToggle={() => toggleItem(child.id)} />;
        });
    };

    const handleSave = async () => {
        setIsSaving(true);
        try {
            await onSave(selected);
            onClose();
        } catch (error) {
            console.error('Failed to save connected flows:', error);
            modalStore.showErrorMessage(generalStore.translate('failedToSaveConnectedFlows') || 'Failed to save connected flows.');
        }
        setIsSaving(false);
    };

    const addedCount = [...selected].filter((id) => !initialSelected.includes(id)).length;
    const removedCount = initialSelected.filter((id) => !selected.has(id)).length;

    return (
        <Dialog open={isOpen} onClose={onClose} maxWidth={false} PaperProps={dialogPaperProps}>
            <div className="flex items-center justify-between px-4 py-2.5">
                <span className="text-base font-semibold text-main-normal">Connect flows</span>
                <CloseIcon onClick={onClose} className="text-main-hover cursor-pointer hover:text-main-normal" style={{fontSize: 18}} />
            </div>
            <SearchBar onSearch={setSearch} placeholder={generalStore.translate('searchContent') || 'Search content...'} />
            <div className="flex-1 overflow-y-auto mx-4 mb-2 p-1 rounded-menu border border-panels-normal bg-windowsandmenus-normal">
                <FolderRow folder={{name: projectName}} depth={0} isExpanded={expanded.has('root')} checkboxState={computeFolderState(childrenIndex, 'root', selected)} onToggleExpand={() => toggleExpand('root')} onToggleCheck={() => toggleFolder('root')} />
                {expanded.has('root') && renderChildren('root')}
            </div>
            <AccessFooter showSummary={false} totalSelected={selected.size} addedCount={addedCount} removedCount={removedCount} onCancel={onClose} onSave={handleSave} isSaving={isSaving} />
        </Dialog>
    );
});

export default ConnectedFlowsModal;
